import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Building2, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../../components/ui/sonner";
import { api } from "../../lib/api";
import { setBranding, withAlpha } from "../../lib/branding";

export default function PortalLogin() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [branding, setLocal] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/portal/branding/${slug}`).then(({ data }) => {
      setLocal(data);
      setBranding(data);
    }).catch((err) => {
      setLoadErr(err?.response?.status === 404 ? "Portal not found" : "Could not load portal");
    });
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      if (data.user.role !== "client" || data.user.client_id == null) {
        toast.error("This account doesn't have portal access");
        return;
      }
      localStorage.setItem("ra_token", data.token);
      localStorage.setItem("ra_user", JSON.stringify(data.user));
      toast.success(`Welcome back to ${branding?.name || "your portal"}`);
      if (data.user.must_change_password) {
        nav(`/portal/${slug}/change-password`);
      } else {
        nav(`/portal/${slug}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Portal not found state ───
  if (loadErr) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white/90 flex items-center justify-center px-6" data-testid="portal-not-found">
        <div className="text-center max-w-md">
          <ShieldAlert className="w-12 h-12 text-rose-400 mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-xl font-semibold mb-2">Portal not available</div>
          <p className="text-sm text-white/55">{loadErr}. Double-check the link or contact your account manager.</p>
        </div>
      </div>
    );
  }

  const accent = branding?.accent_color || "#FFB000";
  const clientName = branding?.name || "...";
  const logoUrl = branding?.logo_url;

  return (
    <div
      data-testid="portal-login-page"
      className="min-h-screen bg-[#0a0a0b] text-white/95 flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: `radial-gradient(ellipse at top, ${withAlpha(accent, 0.12)}, transparent 60%)`,
      }}
    >
      <div
        className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none"
        style={{ background: withAlpha(accent, 0.07) }}
      />
      <div className="w-full max-w-[440px] px-6 relative">
        <div className="flex items-center gap-3 mb-10">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden border"
            style={{
              background: logoUrl ? "transparent" : withAlpha(accent, 0.15),
              borderColor: withAlpha(accent, 0.3),
            }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt={clientName} className="w-full h-full object-contain" data-testid="portal-login-logo" />
            ) : (
              <Building2 className="w-6 h-6" style={{ color: accent }} strokeWidth={1.5} />
            )}
          </div>
          <div>
            <div className="text-white text-xl font-semibold tracking-tight leading-none" data-testid="portal-login-client-name">
              {clientName}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] mt-1.5" style={{ color: withAlpha(accent, 0.7) }}>
              AI Front Desk
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-medium tracking-tight text-white mb-2">Sign in</h1>
          <p className="text-sm text-white/50">Watch every call, booking, and AI conversation in real time.</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="portal-login-form">
          <div>
            <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">Email</label>
            <input
              data-testid="portal-login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#121214] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 placeholder:text-white/30 focus:outline-none focus:ring-1 transition-all"
              style={{ "--tw-ring-color": accent }}
              onFocus={(e) => { e.target.style.borderColor = accent; e.target.style.boxShadow = `0 0 0 1px ${accent}`; }}
              onBlur={(e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">Password</label>
            <input
              data-testid="portal-login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-[#121214] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 placeholder:text-white/30 focus:outline-none focus:ring-1 transition-all"
              onFocus={(e) => { e.target.style.borderColor = accent; e.target.style.boxShadow = `0 0 0 1px ${accent}`; }}
              onBlur={(e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
              placeholder="••••••••••"
            />
          </div>
          <button
            data-testid="portal-login-submit-btn"
            type="submit"
            disabled={submitting || !branding}
            className="w-full text-black font-semibold text-sm px-4 py-2.5 rounded-md transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            style={{
              background: accent,
              boxShadow: `0 0 24px ${withAlpha(accent, 0.25)}`,
            }}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-white/5 text-center">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/25">
            Secure portal · Powered by Repliant Arc
          </p>
        </div>
      </div>
      <Toaster theme="dark" richColors position="top-right" />
    </div>
  );
}
